/**
 * Spec 021 US3 (T027 / FR-016) — ways-win reconstruction for the viewer.
 *
 * A ways game (`evaluation.type: "ways"`) has no `lines` array to draw,
 * so the viewer can't reuse the payline overlay. Instead it reconstructs
 * each spin's ways wins directly from the spun grid + the compiled game's
 * paytable / symbol roles (both carried on the in-browser IR), mirroring
 * the reference engine's `evaluate_ways` (spec 021 T011, contract §3):
 *
 *   1. **Per-reel matching count** `c_i` — positions in reel `i`'s window
 *      holding `S` or a substituting `WILD` (a wild's own run counts only
 *      literal wilds — no self-substitution).
 *   2. **Longest left-aligned prefix** `K` — the largest run where every
 *      reel `0..K` has `c_i ≥ 1`. Strictly left-anchored (`c_0 == 0 ⇒
 *      K = 0`). Only the longest prefix pays per symbol.
 *   3. **Ways count + win** — `W = c_0 × … × c_{K-1}`; the win is
 *      `W × paytable[S][K]` when `K ≥ MIN_RUN` and the paytable entry
 *      exists.
 *
 * The wild-overlap collision (a pure-wild prefix counting toward every
 * regular symbol *and* the wild's own run) resolves by the inherited
 * spec 001.1 `evaluation.mode` (FR-005): `highest_wins` keeps the wild's
 * own win only when it strictly exceeds the best regular win; `all_pays`
 * keeps everything. This is a *presentation* reconstruction — it needs no
 * `MetricsResult` change (R7); the metrics still come from the aggregator.
 */

import type { ReplayedOutcome } from "./types";

/** Minimum run length that can pay — mirrors `MIN_LINE_COUNT` (reference). */
export const MIN_WAYS_RUN = 3;

/** Wild-overlap resolution mode, inherited from spec 001.1 `evaluation.mode`. */
export type WaysMode = "highest_wins" | "all_pays";

/** One symbol's ways win on a single grid. */
export interface WaysWin {
  /** The leading (and only) symbol that paid this win. */
  symbol: string;
  /** Run length `K` — the longest consecutive left-aligned prefix. */
  runLength: number;
  /** Ways count `W = c_0 × … × c_{K-1}` (number of matching paths). */
  waysCount: number;
  /** Per-reel matching counts over the paying prefix (`length === runLength`). */
  perReelCounts: number[];
  /** The `(reel, row)` cells that actually matched over the paying prefix —
   * the symbol's own cells plus substituting wilds (not the whole column). */
  positions: Array<[number, number]>;
  /** Post-multiply payout in stake multiples (`W × paytable[S][K]`). */
  payout: number;
  /** True when `symbol` is the wild's own run (vs. a regular symbol). */
  isWild: boolean;
}

/**
 * The ways-relevant slice of a compiled game, read from the in-browser
 * IR (`ir.evaluation` / `ir.paytable` / `ir.symbols`). `null` when the
 * game is not a ways game (or the IR lacks the fields), so the caller
 * gates the ways affordance off cleanly.
 */
export interface WaysGameInfo {
  mode: WaysMode;
  /** Symbol → run-length → nominal pay (stake multiples). */
  paytable: Map<string, Map<number, number>>;
  /** Wild symbol name → the set of symbols it substitutes for. */
  wilds: Map<string, Set<string>>;
  /** Names of scatter / bonus symbols — excluded from ways (count-based). */
  excluded: Set<string>;
}

/** True when a compiled game's IR declares `evaluation.type: "ways"`. */
export function isWaysGame(
  ir: Record<string, unknown> | null | undefined,
): boolean {
  if (ir == null) return false;
  const evaluation = ir["evaluation"];
  return (
    typeof evaluation === "object" &&
    evaluation !== null &&
    (evaluation as Record<string, unknown>)["type"] === "ways"
  );
}

/**
 * Read the ways-relevant slice of a compiled game from its IR. Returns
 * `null` for a non-ways game or an IR missing the paytable / symbols —
 * the caller then hides the ways panel.
 */
export function readWaysGame(
  ir: Record<string, unknown> | null | undefined,
): WaysGameInfo | null {
  if (!isWaysGame(ir) || ir == null) return null;

  const evaluation = ir["evaluation"] as Record<string, unknown>;
  const mode: WaysMode =
    evaluation["mode"] === "all_pays" ? "all_pays" : "highest_wins";

  const paytableRaw = ir["paytable"];
  const symbolsRaw = ir["symbols"];
  if (
    typeof paytableRaw !== "object" ||
    paytableRaw === null ||
    typeof symbolsRaw !== "object" ||
    symbolsRaw === null
  ) {
    return null;
  }

  const paytable = new Map<string, Map<number, number>>();
  for (const [sym, table] of Object.entries(
    paytableRaw as Record<string, unknown>,
  )) {
    if (typeof table !== "object" || table === null) continue;
    const runs = new Map<number, number>();
    for (const [run, pay] of Object.entries(table as Record<string, unknown>)) {
      const k = Number(run);
      if (
        Number.isInteger(k) &&
        typeof pay === "number" &&
        Number.isFinite(pay)
      ) {
        runs.set(k, pay);
      }
    }
    paytable.set(sym, runs);
  }

  const wilds = new Map<string, Set<string>>();
  const excluded = new Set<string>();
  for (const [name, defRaw] of Object.entries(
    symbolsRaw as Record<string, unknown>,
  )) {
    if (typeof defRaw !== "object" || defRaw === null) continue;
    const def = defRaw as Record<string, unknown>;
    const kind = def["type"];
    if (kind === "wild") {
      const subs = Array.isArray(def["substitutes"])
        ? (def["substitutes"] as unknown[]).filter(
            (s): s is string => typeof s === "string",
          )
        : [];
      wilds.set(name, new Set(subs));
    } else if (kind === "scatter" || kind === "bonus_trigger") {
      excluded.add(name);
    }
  }

  return { mode, paytable, wilds, excluded };
}

/**
 * Count positions in one reel's window matching `base` under ways
 * (contract §3.1). A regular `base` matches its own cells plus any wild
 * whose `substitutes` include it; a wild `base` matches only literal
 * wild cells (no self-substitution). Mirrors reference `_ways_reel_count`.
 */
function matchingRows(
  reel: readonly string[],
  base: string,
  isWild: boolean,
  info: WaysGameInfo,
): number[] {
  const rows: number[] = [];
  for (let row = 0; row < reel.length; row++) {
    const sym = reel[row];
    if (sym === base) {
      rows.push(row);
      continue;
    }
    if (isWild) continue;
    const subs = sym === undefined ? undefined : info.wilds.get(sym);
    if (subs !== undefined && subs.has(base)) rows.push(row);
  }
  return rows;
}

/**
 * Reconstruct every ways win on one reel-major grid, ordered
 * highest-paying first (the leading win). Mirrors `evaluate_ways` +
 * `_resolve_ways_overlap` (reference, spec 021 T011/T012): each regular
 * and wild symbol's longest left-aligned prefix pays independently, then
 * the wild's own run is resolved against the best regular win per `mode`.
 */
export function extractWaysWins(
  grid: string[][],
  info: WaysGameInfo,
): WaysWin[] {
  const reelCount = grid.length;
  const regularWins: WaysWin[] = [];
  const wildWins: WaysWin[] = [];

  // Candidate symbols: every paytable symbol that is not scatter/bonus
  // (regular + wild), mirroring the reference's `kind in (REGULAR, WILD)`.
  for (const [symbol, runs] of info.paytable) {
    if (info.excluded.has(symbol)) continue;
    const isWild = info.wilds.has(symbol);

    let waysCount = 1;
    const perReelCounts: number[] = [];
    const positions: Array<[number, number]> = [];
    for (let reel = 0; reel < reelCount; reel++) {
      const rows = matchingRows(grid[reel] ?? [], symbol, isWild, info);
      if (rows.length === 0) break;
      waysCount *= rows.length;
      perReelCounts.push(rows.length);
      for (const row of rows) positions.push([reel, row]);
    }
    const runLength = perReelCounts.length;
    if (runLength < MIN_WAYS_RUN) continue;

    const pay = runs.get(runLength);
    if (pay === undefined) continue;

    const win: WaysWin = {
      symbol,
      runLength,
      waysCount,
      perReelCounts,
      positions,
      payout: waysCount * pay,
      isWild,
    };
    (isWild ? wildWins : regularWins).push(win);
  }

  const wins = [...regularWins];
  if (info.mode === "all_pays") {
    wins.push(...wildWins);
  } else {
    // highest_wins: keep the wild's own run only when it strictly beats
    // the best regular win (ties discount the wild — spec 001.1 order).
    let bestRegular = -1;
    for (const w of regularWins) bestRegular = Math.max(bestRegular, w.payout);
    for (const w of wildWins) if (w.payout > bestRegular) wins.push(w);
  }

  wins.sort((a, b) => b.payout - a.payout);
  return wins;
}

/** A sampled spin's most illustrative ways win, with the grid it paid on. */
export interface WaysOutcomePick {
  outcome: ReplayedOutcome;
  index: number;
  /** The evaluate grid the wins were reconstructed from. */
  grid: string[][];
  /** The ways wins on that grid, highest-paying first. */
  wins: WaysWin[];
}

/**
 * Pick the most illustrative ways win from a sample: the spin whose
 * best-paying evaluate grid yields the largest total ways payout. A
 * spin's ways win can land on any `evaluate` step's grid (base spin or a
 * free-spin re-evaluate), so every per-evaluate grid is a candidate — the
 * same multi-evaluate `state_path` the cascade view reads. Returns `null`
 * when no sampled spin produced a ways win (the panel then stays hidden).
 */
export function pickWaysOutcome(
  outcomes: ReplayedOutcome[],
  info: WaysGameInfo,
): WaysOutcomePick | null {
  let best: WaysOutcomePick | null = null;
  let bestTotal = 0;

  for (let i = 0; i < outcomes.length; i++) {
    const outcome = outcomes[i];
    if (outcome === undefined) continue;

    for (const grid of candidateGrids(outcome)) {
      const wins = extractWaysWins(grid, info);
      if (wins.length === 0) continue;
      let total = 0;
      for (const w of wins) total += w.payout;
      if (total > bestTotal) {
        bestTotal = total;
        best = { outcome, index: i, grid, wins };
      }
    }
  }

  return best;
}

/** Every grid a spin's ways wins could land on: each evaluate's grid,
 * with the end-of-spin grid as a fallback for a synthetic `state_path`. */
function candidateGrids(outcome: ReplayedOutcome): string[][][] {
  const grids: string[][][] = [];
  for (const step of outcome.state_path) {
    if (step.action_kind !== "evaluate") continue;
    const grid = asGrid(step.detail?.["grid"]);
    if (grid !== null) grids.push(grid);
  }
  if (grids.length === 0) grids.push(outcome.grid);
  return grids;
}

function asGrid(value: unknown): string[][] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const grid: string[][] = [];
  for (const column of value) {
    if (!Array.isArray(column)) return null;
    grid.push(column.map(String));
  }
  return grid;
}
